type validateMessageTypeParams = {
  min?: string | number
  max?: string | number
  length?: string | number
  domainName?: string
  field?: string
}

type validateMessageGetter = (params: validateMessageTypeParams) => string

type validateMessageType = {
  required: validateMessageGetter
  minLength: validateMessageGetter
  maxLength: validateMessageGetter
  anyMaxLength: validateMessageGetter
  between: validateMessageGetter
  anyBetween: validateMessageGetter
  anyJustLength: validateMessageGetter
  alphaNum: validateMessageGetter
  alphaNumMix: validateMessageGetter
  numeric: validateMessageGetter
  alphaNumSymbol: validateMessageGetter
  email: validateMessageGetter
  emoji: validateMessageGetter
  emailDomain: validateMessageGetter
  char4Byte: validateMessageGetter
  katakana: validateMessageGetter
  noSymbolUserName: validateMessageGetter
  url: validateMessageGetter
  isParentalConsent: validateMessageGetter
  isPast: validateMessageGetter
  phoneNumber: validateMessageGetter
  smsPhoneNumber: validateMessageGetter
  luhnChecksum: validateMessageGetter
  expire: validateMessageGetter
  requiredChange: validateMessageGetter
  availableCardBrand: validateMessageGetter
  betweenYen: validateMessageGetter
}

/*
 常に表示されているメッセージ
 ex.
  ✔︎ 入力必須
  × 入力必須
*/
const alwaysDisplayedMessages: validateMessageType = {
  required: () => '入力必須',
  minLength: ({ min }) => `${min}文字以上`,
  maxLength: ({ max }) => `${max}文字以下`,
  // 0文字許容
  anyMaxLength: ({ max }) => `${max}文字以下`,
  between: ({ min, max }) => `${min}文字以上${max}文字以下`,
  // 0文字許容
  anyBetween: ({ min, max }) => `${min}文字以上${max}文字以下`,
  anyJustLength: ({ length }) => `${length}文字`,
  alphaNum: () => '半角英数字',
  alphaNumMix: () => '英数混合必須',
  numeric: () => '数値のみ',
  alphaNumSymbol: () => '半角英数記号のみ',
  // TODO:メールアドレスの文言は相談
  email: () => 'メールアドレス以外の形式不可',
  emoji: () => '絵文字不可',
  emailDomain: ({ domainName }) => domainName,
  char4Byte: () => '4byte絵文字不可',
  katakana: () => 'カタカナのみ',
  noSymbolUserName: () => '記号不可',
  url: () => 'URL以外の形式不可',
  isParentalConsent: () => '親権者の同意が必要なため入力してください',
  isPast: () => '正しい日付を選択してください',
  phoneNumber: () => '正しい番号を入力してください',
  smsPhoneNumber: () =>
    'SMS（ショートメッセージサービス）が利用できる日本国内の携帯電話番号を入力してください',
  luhnChecksum: ({ field }) =>
    field ? `正しい${field}を入力してください` : '正しいカード番号を入力してください',
  expire: ({field}) => (field ? `${field}の年月が過ぎています` : '年月が過ぎています'),
  requiredChange: ({field}) => (field ? `${field}を変更してください` : '変更してください'),
  availableCardBrand: () => 'この出品者へのお支払いではこのカードをご利用いただけません',
  betweenYen: ({ min, max }) => `${min}円〜${max}円`
}

/*
 バリデーション発火時に表示されるのみにメッセージ
*/
const validateMessages: validateMessageType = {
  required: ({ field }) => (field ? `${field}を入力してください。` : '入力してください。'),
  minLength: ({ min, field }) =>
    field ? `${field}は${min}文字以上で入力してください。` : `${min}文字以上で入力してください。`,
  maxLength: ({ max, field }) =>
    field ? `${field}は${max}文字以下で入力してください。` : `${max}文字以下で入力してください。`,
  // 0文字許容
  anyMaxLength: ({ max }) => `${max}文字以下で入力してください。`,
  between: ({ min, max }) => `${min}文字以上${max}文字以下で入力してください。`,
  // 0文字許容
  anyBetween: ({ min, max }) => `${min}文字以上${max}文字以下で入力してください。`,
  anyJustLength: ({ length }) => `${length}桁の番号を入力してください。`,
  alphaNum: () => '半角英数字で入力してください。',
  alphaNumMix: () => '英字・数字それぞれ含めて入力してください。',
  numeric: ({ field }) =>
    field ? `${field}は数字のみで入力してください。` : '数字のみで入力してください。',
  alphaNumSymbol: () => '半角英数字と記号のみ入力可能です。',
  email: () => 'メールアドレスを入力してください。',
  emoji: () => '絵文字は入力できません。',
  emailDomain: ({ domainName }) => domainName,
  char4Byte: () => '絵文字は入力できません。',
  katakana: () => 'カタカナで入力してください。',
  noSymbolUserName: () => '記号は入力できません。',
  url: () => 'URLを入力してください。',
  isParentalConsent: () => '親権者の同意が必要なため入力してください。',
  isPast: () => '正しい日付を選択してください',
  phoneNumber: () => '正しい番号を入力してください。',
  smsPhoneNumber: () =>
    'SMS（ショートメッセージサービス）が利用できる日本国内の携帯電話番号を入力してください',
  luhnChecksum: ({ field }) =>
    field ? `正しい${field}を入力してください` : '正しいカード番号を入力してください',
  expire: ({field}) => (field ? `${field}の年月が過ぎています` : '年月が過ぎています'),
  requiredChange: ({field}) => (field ? `${field}を変更してください` : '変更してください'),
  availableCardBrand: () => 'この出品者へのお支払いではこのカードをご利用いただけません',
  betweenYen: ({ min, max }) =>
    `${min.toLocaleString()}円〜${max.toLocaleString()}円の範囲で入力してください`
}

export default (
  type: keyof validateMessageType,
  params: validateMessageTypeParams,
  field?: string
): {
  alwaysDisplayedMessage: string
  validateMessage: string
} => {
  if (!(alwaysDisplayedMessages[type] && validateMessages[type])) {
    // js、vueからの呼び出し可能性もあるので、存在チェック
    throw new Error(`validate_message_type:${type} is not a function`)
  }

  const mergedParams = field ? { ...params, field } : params
  return {
    alwaysDisplayedMessage: alwaysDisplayedMessages[type](mergedParams),
    validateMessage: validateMessages[type](mergedParams)
  }
}
